import React, { useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Grid,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useParams } from 'react-router-dom';
import FaqCambridgeIgcse from '../../constants/PastPapers/FAQ/FaqCambridgeIgcse';
import FaqEdexcelIgcse from '../../constants/PastPapers/FAQ/FaqEdexcelIgcse';

const Faq: React.FC = () => {
  const params = useParams();
  const curriculum = params.curriculum?.trim();
  const theme = useTheme();
  const belowMd = useMediaQuery(theme.breakpoints.down('md'));

  const [expanded, setExpanded] = useState<string | false>(false);
  const [selectedTopic, setSelectedTopic] = useState<number>(0);

  const handleChange = (panel: string) => (
    event: React.SyntheticEvent,
    newExpanded: boolean
  ) => {
    setExpanded(newExpanded ? panel : false);
  };

  const handleTopicSelect = (index: number) => {
    setSelectedTopic(index);
    setExpanded(false);
  };

  let faqContent: any[] = [];
  switch (curriculum) {
    case 'cambridge-igcse':
      faqContent = FaqCambridgeIgcse;
      break;
    case 'edexcel-igcse':
      faqContent = FaqEdexcelIgcse;
      break;
    default:
      faqContent = [];
      break;
  }

  return (
    <Grid
      container
      alignItems="stretch"
      sx={{
        borderRadius: '20px',
        backgroundColor: '#F8FAFC',
        maxWidth: !belowMd ? '75%' : '100%',
        justifySelf: 'center',
        border: '1px dashed #CBD5E1',
      }}
    >
      {!belowMd ? (
        <>
          <Grid
            item
            xs={12}
            lg={4}
            sx={{
              borderRight: !belowMd ? '1px dashed #CBD5E1' : 'none',
            }}
          >
            {faqContent.map((topic, index) => (
              <Box
                key={index}
                sx={{
                  backgroundColor:
                    selectedTopic === index ? '#E2E8F0' : '#F1F5F9',
                  borderRadius: '10px',
                  m: '10px',
                  p: '15px',
                  cursor: 'pointer',
                  textAlign: 'left',
                  '&:hover': {
                    backgroundColor: '#E2E8F0',
                  },
                }}
                onClick={() => handleTopicSelect(index)}
              >
                <Typography fontWeight={500}>{topic.title}</Typography>
              </Box>
            ))}
          </Grid>
          <Grid
            item
            xs={12}
            lg={8}
            border="none"
            sx={{
              marginTop: belowMd ? '20px' : '0',
            }}
          >
            {faqContent[selectedTopic]?.content.map((item, index) => {
              const itemId = `panel-${index + 1}`;
              const _styles: React.CSSProperties = {
                margin: 0,
                backgroundColor: '#F8FAFC',
                borderTop: index === 0 ? 'none' : '1px solid #E5E7EB',
              };

              if (index === 0) {
                delete _styles.borderTop;
                _styles.borderTopLeftRadius = '20px';
                _styles.borderTopRightRadius = '20px';
              }

              if (index === faqContent[selectedTopic].content.length - 1) {
                delete _styles.borderBottom;
                _styles.borderBottomLeftRadius = '20px';
                _styles.borderBottomRightRadius = '20px';
              }

              return (
                <React.Fragment key={`faq-item-${index + 1}`}>
                  <Accordion
                    elevation={0}
                    expanded={expanded === itemId}
                    style={_styles}
                    onChange={handleChange(itemId)}
                    sx={{
                      '&:before': {
                        backgroundColor: 'transparent !important',
                      },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<KeyboardArrowDownIcon />}
                      style={{ padding: '15px', border: 'none' }}
                    >
                      <Typography fontWeight={500} color="#334155">
                        {item.question}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails style={{ border: 'none' }}>
                      <Typography color="#647488" variant="subtitle2">
                        {item.answer}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </React.Fragment>
              );
            })}
          </Grid>
        </>
      ) : (
        faqContent.map((topic, topicIndex) => (
          <Box key={`topic-${topicIndex}`} sx={{ marginBottom: '20px' }}>
            <Box
              sx={{
                backgroundColor: '#E2E8F0',
                borderRadius: '10px',
                m: '10px',
                p: '15px',
                textAlign: 'left',
              }}
            >
              <Typography fontWeight={500}>{topic.title}</Typography>
            </Box>
            {topic.content.map((item, index) => {
              const itemId = `panel-${topicIndex}-${index + 1}`;
              return (
                <React.Fragment key={`faq-item-${itemId}`}>
                  <Accordion
                    elevation={0}
                    expanded={expanded === itemId}
                    onChange={handleChange(itemId)}
                    sx={{
                      marginBottom: '10px',
                      '&:before': {
                        backgroundColor: 'transparent !important',
                      },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<KeyboardArrowDownIcon />}
                      style={{ padding: '15px', border: 'none' }}
                    >
                      <Typography fontWeight={500} color="#334155">
                        {item.question}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails style={{ border: 'none' }}>
                      <Typography color="#647488" variant="subtitle2">
                        {item.answer}
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </React.Fragment>
              );
            })}
          </Box>
        ))
      )}
    </Grid>
  );
};

export default Faq;
